<template>
  <el-card v-loading="loading" class="advanced-search-condition-container">
    <el-scrollbar wrap-class="scrollbar-wrapper" class="condition-wrapper">
      <div class="title">Advanced Search Condition</div>
      <div class="line-box list-header-title">
        <el-row :gutter="10">
          <el-col :span="4">And / Or</el-col>
          <el-col :span="5">Fields</el-col>
          <el-col :span="4">Operator</el-col>
          <el-col :span="11">Value</el-col>
        </el-row>
        <el-button type="text" size="medium" @click="addConditionGroup">Add New Group</el-button>
      </div>
      <div v-for="(item1, index1) in queryList" :key="index1">
        <apex-select
          v-if="index1 !== 0"
          v-model="item1.andOr"
          size="mini"
          :options="andOrList"
          :clearable="false"
          :filterable="false"
          style="margin: 10px 0; width: 20%"
        />
        <div class="group-wrapper">
          <div
            v-for="(item2, index2) in item1.innerList"
            :key="index2"
            class="line-box"
            :class="{ 'mb-0': index2 === item1.innerList.length - 1 }"
          >
            <el-row :gutter="10">
              <el-col :span="4">
                <apex-select
                  v-model="item2.andOr"
                  size="mini"
                  :options="andOrList"
                  :clearable="false"
                  :filterable="false"
                  :style="{ visibility: index2 === 0 ? 'hidden' : '' }"
                />
              </el-col>
              <el-col :span="5">
                <apex-select
                  v-model="item2.field"
                  size="mini"
                  placeholder="Please select"
                  :options="fieldsList"
                  :clearable="false"
                  :automatic-dropdown="true"
                  :props="{ label: 'text', value: 'key' }"
                  @change="val => fieldChange(index1, index2)"
                />
              </el-col>
              <el-col :span="4">
                <apex-select
                  v-model="item2.operator"
                  size="mini"
                  placeholder="Please select"
                  :options="operatorList"
                  :clearable="false"
                  :filterable="false"
                  @change="operatorChange(item2)"
                />
              </el-col>
              <el-col :span="11">
                <el-input
                  v-if="getCheckField(item2.field, 'type') === '0'"
                  v-model="item2.value"
                  clearable
                  :disabled="item2.operator === 'Is Null'"
                  size="mini"
                />
                <apex-date-picker
                  v-if="getCheckField(item2.field, 'type') === '1' && item2.operator !== 'Between'"
                  key="radio_date"
                  v-model="item2.value"
                  type="datetime"
                  format="yyyy-MM-dd HH:mm:ss"
                  placeholder="Select date"
                  :disabled="item2.operator === 'Is Null'"
                  size="mini"
                />
                <apex-date-picker
                  v-if="getCheckField(item2.field, 'type') === '1' && item2.operator === 'Between'"
                  key="range_date"
                  v-model="item2.value"
                  type="datetimerange"
                  format="yyyy-MM-dd HH:mm:ss"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  :default-time="['00:00:00', '23:59:59']"
                  :disabled="item2.operator === 'Is Null'"
                  size="mini"
                />
                <apex-select
                  v-if="getCheckField(item2.field, 'type') === '2'"
                  key="radio_select"
                  v-model="item2.value"
                  :options="selectListObj[getCheckField(item2.field, 'searchField')] || []"
                  :disabled="item2.operator === 'Is Null'"
                  placeholder="Please select"
                  size="mini"
                  remote
                  :local-filter="{ max: 50 }"
                />
                <apex-select
                  v-if="getCheckField(item2.field, 'type') === '3'"
                  key="multiple_select"
                  v-model="item2.value"
                  :options="selectListObj[getCheckField(item2.field, 'searchField')] || []"
                  :disabled="item2.operator === 'Is Null'"
                  placeholder="Please select"
                  size="mini"
                  multiple
                  remote
                  :local-filter="{ max: 50 }"
                />
              </el-col>
            </el-row>
            <el-button
              type="text"
              size="mini"
              :disabled="item1.innerList.length === 1"
              @click="removeConditionLine(index1, index2)"
            >Remove</el-button>
          </div>
          <div>
            <el-button type="text" @click="addConditionLine(index1)">Add New Filter Line</el-button>
            <el-button type="text" :disabled="queryList.length === 1" @click="removeConditionGroup(index1)">Remove Current Group</el-button>
          </div>
        </div>
      </div>
    </el-scrollbar>
    <el-scrollbar wrap-class="scrollbar-wrapper" class="search-template-wrapper">
      <div class="title">
        <span>Search Template</span>
        <div class="default-template">
          <i class="el-icon-circle-check" />
          <span>Default Template</span>
        </div>
      </div>
      <div id="id-tags" class="tags-wrapper">
        <el-tag
          v-for="(template, index) in templateList"
          :key="index"
          :title="template.name"
          :class="{ 'active-tag': activeTagId === template.id, 'default-tag': defaultTagId === template.id }"
          size="small"
          closable
          @click="handleClickTemplate(template)"
          @contextmenu.native="e => handleRightClick(template, e)"
          @close="deleteTemplate(template)"
        >{{ template.name }}</el-tag>
      </div>
      <img src="../../img/up arrow.png" :title="t('tableFilter.packUp')" @click="handleAdvancedSearchExpanded(false)">
    </el-scrollbar>
    <div class="operation-btn-wrapper">
      <el-button size="small" icon="el-icon-search" type="primary" @click="handleSearch">Search</el-button>
      <el-button size="small" icon="el-icon-refresh" @click="handleReset">Reset</el-button>
      <el-button size="small" type="primary" @click="saveTemplate">Save Template</el-button>
    </div>

    <v-contextmenu ref="refContextmenu">
      <v-contextmenu-item @click="setAsDefaultTemplate">
        <i class="el-icon-circle-check" style="margin-right: 2px;" />
        <span>Set as Default</span>
      </v-contextmenu-item>
    </v-contextmenu>

    <apex-dialog
      title="Save Search Template"
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      :fullscreen="false"
      :drag="false"
      append-to-body
      width="500px"
      center
      size="medium"
      confirm-text="Save"
      @confirm="saveTemplateFn"
      @cancel="dialogVisible = false"
      @close="closeDialog"
    >
      <el-form ref="refForm" :model="templateObj" :rules="rules">
        <el-form-item prop="name">
          <el-input v-model="templateObj.name" placeholder="Please input template name" clearable />
        </el-form-item>
        <el-checkbox v-model="templateObj.isDefault" :true-label="1" :false-label="0">Set as default</el-checkbox>
      </el-form>
    </apex-dialog>
  </el-card>
</template>

<script>
import locale from '@/mixins/index'
import { Contextmenu, ContextmenuItem } from 'v-contextmenu'
import 'v-contextmenu/dist/index.css'

export default {
  components: {
    [Contextmenu.name]: Contextmenu,
    [ContextmenuItem.name]: ContextmenuItem
  },
  mixins: [locale],
  props: {
    // 高级查询展开折叠状态
    advancedSearchExpanded: {
      type: Boolean,
      default: false
    },
    operatorList: {
      type: Array,
      default: () => [
        { label: '=', value: '=' },
        { label: '<>', value: '<>' },
        { label: '>', value: '>' },
        { label: '<', value: '<' },
        { label: '>=', value: '>=' },
        { label: '<=', value: '<=' },
        { label: 'Contains', value: 'Contains' },
        { label: 'Exclude', value: 'Exclude' },
        { label: 'Start With', value: 'Start With' },
        { label: 'End With', value: 'End With' },
        { label: 'Is Blank', value: 'Is Blank' },
        { label: 'Is Not Blank', value: 'Is Not Blank' },
        { label: 'In', value: 'In' },
        { label: 'Not in', value: 'Not in' },
        { label: 'Between', value: 'Between' }
      ]
    },
    andOrList: {
      type: Array,
      default: () => [
        { label: 'And', value: 'and' },
        { label: 'Or', value: 'or' }
      ]
    }
  },
  data() {
    const templateValidator = (rule, value, callback) => {
      // 1. 高级搜索模块保存搜索模板时，新增模板名称校验提示
      // 2. 提示模板名称重复后仍然保存，则覆盖原有模板内搜索条件
      if (!value) {
        return callback(new Error('is required'))
      }
      const obj = this.templateList.find(item => item.name.toUpperCase() === value.toUpperCase())
      if (obj && !this.validFlag) {
        this.validFlag = true
        this.saveId = obj.id
        callback(new Error('Duplicate，Save to overwrite'))
      } else {
        if (this.validFlag) {
          this.validFlag = false
        } else {
          this.saveId = ''
        }
        callback()
      }
    }
    return {
      loading: false,
      dialogVisible: false,
      templateObj: {
        name: '',
        isDefault: 0
      },
      defaultTagId: null,
      activeTagId: null,
      contextRowData: null,
      selectListObj: {},
      rules: {
        name: [
          { validator: templateValidator, trigger: 'change' }
        ]
      },
      queryList: [
        {
          andOr: 'and',
          innerList: [
            { andOr: 'and', field: '', operator: '', value: '' },
            { andOr: 'and', field: '', operator: '', value: '' },
            { andOr: 'and', field: '', operator: '', value: '' }
          ]
        }
      ],
      fieldsList: [],
      templateList: []
      // fieldsList: [
      //   { key: '字段111', text: 'test1', type: 0 },
      //   { key: '字段222', text: 'test2', type: 1 },
      //   { key: '字段333', text: 'test3', type: 2 }
      // ],
      // templateList: [
      //   { name: 'Amazon Group', id: 1 },
      //   { name: 'Amazon Group2222', id: 2 },
      //   { name: 'Amazon Group3333', id: 3 },
      //   { name: 'Amazon Group-test-2022-07-19 Group-test-2022-07-19', id: 4 },
      //   { name: 'Amazon Group', id: 5 },
      //   { name: 'Amazon Group', id: 6 },
      //   { name: '123', id: 7 },
      //   { name: '345', id: 8 },
      //   { name: '456', id: 9 }
      // ]
    }
  },
  methods: {
    // 设置删除模板的delete icon 的 title
    setDelIconTitle() {
      const domElTags = document.getElementById('id-tags').getElementsByClassName('el-tag')
      Array.from(domElTags).forEach(dom => {
        const elClose = dom.getElementsByClassName('el-tag__close')[0]
        elClose.title = 'Delete'
      })
    },
    // Is Null 的时候value禁用  清空
    operatorChange(row) {
      const isDate1 = this.getCheckField(row.field, 'type') === '1' && row.operator === 'Between'
      const isDate2 = this.getCheckField(row.field, 'type') === '1' && row.operator !== 'Between' && Array.isArray(row.value)
      if (row.operator === 'Is Null' || isDate1 || isDate2) {
        row.value = ''
      }
    },
    // 获取value控件类型
    getCheckField(val, field) {
      const obj = this.fieldsList.find(item => item.key === val)
      if (obj) {
        return obj[field]
      }
      return field === 'type' ? '0' : 'return_undefined'
    },
    fieldChange(index1, index2) {
      const row = this.queryList[index1].innerList[index2]
      row.value = ''
      // 如果缓存中有就取缓存的
      const obj = this.fieldsList.find(item => item.key === row.field)
      if (obj.type === '2' || obj.type === '3') {
        if (this.selectListObj[obj.searchField] && this.selectListObj[obj.searchField].length === 0) {
          this.$emit('fieldChange', obj)
        }
      }
    },
    setSelectListObj(key, value) {
      this.$set(this.selectListObj, key, value)
    },
    setFieldsList(list) {
      this.loading = false
      this.fieldsList = list
      // 设置下拉对象
      list.forEach(item => {
        if (item.type === '2' || item.type === '3') {
          this.setSelectListObj(item.searchField, [])
        }
      })
    },
    setTemplateList(list, isInit = false) {
      this.loading = false
      this.templateList = list
      const defaultTagObj = list.find(item => item.isDefault === 1)
      if (defaultTagObj) {
        this.defaultTagId = defaultTagObj.id
        if (isInit) {
          this.handleClickTemplate(defaultTagObj)
        }
      } else {
        this.defaultTagId = null
      }
      this.$nextTick(() => {
        this.setDelIconTitle()
      })
    },
    setQueryList(list) {
      this.loading = false
      this.$set(this, 'queryList', list)
      // this.queryList = list
      list.forEach(item1 => {
        item1.innerList.forEach(item2 => {
          const tempField = this.getCheckField(item2.field, 'searchField')
          if (this.selectListObj[tempField] && this.selectListObj[tempField].length === 0) {
            const obj = this.fieldsList.find(item => item.key === item2.field)
            if (obj.type === '2' || obj.type === '3') {
              this.$emit('fieldChange', obj)
            }
          }
        })
      })
    },
    handleAdvancedSearchExpanded(expanded) {
      this.$emit('expandChange', expanded)
    },
    addConditionGroup() {
      this.queryList.push({
        andOr: 'and',
        innerList: [{ andOr: 'and', field: '', operator: '', value: '' }]
      })
    },
    addConditionLine(index) {
      this.queryList[index].innerList.push({ andOr: 'and', field: '', operator: '', value: '' })
    },
    removeConditionGroup(index) {
      this.queryList.splice(index, 1)
    },
    removeConditionLine(outIndex, innerIndex) {
      this.queryList[outIndex].innerList.splice(innerIndex, 1)
    },
    deleteTemplate(row) {
      this.$confirm('Do you want to delete this template?', 'Message', {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'warning',
        closeOnClickModal: false
      }).then(() => {
        this.$emit('deleteTemplate', row)
      }).catch(() => {})
    },
    saveTemplate() {
      // 被选中模板
      if (this.activeTagId) {
        const obj = this.templateList.find(item => item.id === this.activeTagId)
        this.$confirm(`Do you want to update selected search template named ${obj.name}?`, 'Notification', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning'
        }).then(() => {
          const activeTagObj = this.templateList.find(item => item.id === this.activeTagId)
          if (!activeTagObj) {
            this.$message.warning('No templates are available.')
            return
          }
          const params = {
            queryList: this.queryList,
            id: this.activeTagId,
            name: activeTagObj.name,
            isDefault: activeTagObj.isDefault
          }
          this.$emit('saveTemplate', params)
          this.dialogVisible = false
        }).catch(() => {
          this.saveId = ''
          this.validFlag = false
          this.dialogVisible = true
        })
      } else {
        this.saveId = ''
        this.validFlag = false
        this.dialogVisible = true
      }
    },
    saveTemplateFn() {
      this.$refs.refForm.validate((valid) => {
        if (valid) {
          this.$emit('saveTemplate', { ...this.templateObj, queryList: this.queryList, id: this.saveId })
          this.dialogVisible = false
        }
      })
    },
    closeDialog() {
      this.templateObj.name = ''
      this.templateObj.isDefault = 0
      this.$refs.refForm.resetFields()
    },
    handleSearch() {
      this.$emit('search', this.queryList)
    },
    handleReset() {
      this.clearQueryData()
      this.$emit('reset', this.queryList)
    },
    clearQueryData() {
      const list = [
        {
          andOr: 'and',
          innerList: [
            { andOr: 'and', field: '', operator: '', value: '' },
            { andOr: 'and', field: '', operator: '', value: '' },
            { andOr: 'and', field: '', operator: '', value: '' }
          ]
        }
      ]
      this.$set(this, 'queryList', list)
    },
    handleClickTemplate(row) {
      this.activeTagId = row.id
      this.$emit('clickTemplate', row)
    },
    handleRightClick(row, e) {
      this.contextRowData = row
      e.preventDefault()
      const postition = {
        top: e.clientY,
        left: e.clientX
      }
      this.$refs.refContextmenu.show(postition)
    },
    setAsDefaultTemplate() {
      this.defaultTagId = this.contextRowData.id
      this.$emit('setAsDefaultTemplate', this.contextRowData)
    }
  }
}
</script>

<style lang="scss" scoped>
.mb-0 {
  margin-bottom: 0 !important;
}
.advanced-search-condition-container {
  margin-bottom: 10px;
  .el-input,
  .el-select,
  .el-date-editor {
    width: 100%;
  }
  /deep/ .el-card__body {
    padding: 0;
    display: flex;
    max-height: 440px;
    position: relative;
    .title {
      font-weight: bold;
      margin-bottom: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .default-template {
        font-weight: normal;
        color: #409eff;
        font-size: 13px;
        i {
          margin-right: 2px;
        }
      }
    }
    .condition-wrapper {
      flex: 1;
      border-right: 1px dashed #ccc;
      .scrollbar-wrapper {
        padding: 15px;
        .el-scrollbar__view {
          padding-bottom: 15px;
        }
        .list-header-title {
          font-size: 14px;
          align-items: center;
          padding: 0 12px;
          margin-bottom: 0 !important;
          // color: #40a8ff;
        }
        .group-wrapper {
          border: 1px dashed #40a8ff;
          border-radius: 10px;
          padding: 10px 10px 0;
        }
        .line-box {
          display: flex;
          margin-bottom: 8px;
          .el-row {
            flex: 1;
          }
          .el-button {
            width: 118px;
            margin-left: 10px;
          }
        }
      }
    }
    .search-template-wrapper {
      width: 320px;
      border-right: 1px dashed #ccc;
      .scrollbar-wrapper {
        padding: 15px;
        .el-scrollbar__view {
          padding-bottom: 5px;
        }
        img {
          width: 24px;
          height: 24px;
          cursor: pointer;
          position: absolute;
          bottom: 8px;
          right: 8px;
        }
        .tags-wrapper {
          display: flex;
          flex-wrap: wrap;
          .el-tag {
            position: relative;
            margin: 0 8px 8px 0;
            cursor: pointer;
            padding-right: 16px;
            max-width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            .el-tag__close {
              position: absolute;
              top: 4px;
              right: 0px;
            }
            &.active-tag {
              background-color: #169bd5;
              color: #fff;
            }
            &.default-tag::before {
              content: "\e720";
              font-family: element-icons !important;
              margin-right: 2px;
            }
          }
        }
      }
    }
    .operation-btn-wrapper {
      padding: 15px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      .el-button {
        width: 112px;
        margin: 10px 0 0 0;
        &:nth-of-type(1) {
          margin-top: 0;
        }
      }
    }
  }
}
</style>
